import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  Business,
  DarkMode,
  Email,
  Inventory2,
  Kitchen,
  Language,
  LightMode,
  Lock,
  Person,
  Phone,
  PointOfSale,
  QrCode2,
  Restaurant,
  Visibility,
  VisibilityOff,
  WhatsApp,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeModeContext } from '../hooks/useThemeMode';

const green = '#238d25';
const greenDark = '#0b5f1c';
const greenLight = '#5fc243';
const ink = '#111827';
const muted = '#667085';

const features = [
  { label: 'POS', detail: 'Billing', icon: <PointOfSale /> },
  { label: 'Kitchen', detail: 'Display', icon: <Kitchen /> },
  { label: 'QR', detail: 'Ordering', icon: <QrCode2 /> },
  { label: 'Inventory', detail: 'Management', icon: <Inventory2 /> },
  { label: 'Reports', detail: 'Analytics', icon: <Business /> },
  { label: 'Staff', detail: 'Management', icon: <Person /> },
];

const Login: React.FC = () => {
  const [restaurantCode, setRestaurantCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberRestaurantCode, setRememberRestaurantCode] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { toggleMode, isDark } = useThemeModeContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!restaurantCode || !username || !password) {
      setError('Please enter restaurant code, username, and password');
      return;
    }

    setIsSubmitting(true);
    const success = await login(restaurantCode, username, password, rememberRestaurantCode);
    setIsSubmitting(false);

    if (success) {
      navigate('/dashboard');
    } else {
      setError('Invalid restaurant code, username or password. Please try again.');
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 56,
      borderRadius: '10px',
      background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
      boxShadow: isDark ? 'none' : '0 8px 20px rgba(15,23,42,0.04)',
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.14)' : '#d9dee7' },
      '&:hover fieldset': { borderColor: '#b9d9ba' },
      '&.Mui-focused fieldset': { borderColor: green, borderWidth: '1.5px' },
      '&.Mui-focused': { boxShadow: `0 0 0 4px rgba(35,141,37,0.12)` },
    },
    '& .MuiInputLabel-root': { color: isDark ? '#cbd5e1' : '#344054', fontWeight: 700 },
    '& .MuiInputLabel-root.Mui-focused': { color: green },
    '& .MuiOutlinedInput-input': { color: isDark ? '#f8fafc' : ink, fontWeight: 700 },
    '& .MuiInputAdornment-root svg': { color: green },
  };

  return (
    <Box
      className="login-screen"
      sx={{
        minHeight: '100svh',
        color: isDark ? '#f8fafc' : ink,
        background: isDark
          ? 'linear-gradient(120deg, #07130b, #101827 52%, #0d1b11)'
          : 'linear-gradient(120deg, #edf7ec, #ffffff 48%, #f7fbf7)',
        position: 'relative',
        overflow: 'auto',
        p: { xs: 1.75, md: 2.25, lg: 3 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          color: isDark ? 'rgba(95,194,67,0.08)' : 'rgba(64,132,52,0.08)',
          fontWeight: 900,
          fontSize: 42,
          letterSpacing: 18,
          pointerEvents: 'none',
          display: { xs: 'none', md: 'block' },
          '&::before': { content: '"POS QR KDS MENU STOCK"', position: 'absolute', top: 24, right: 52 },
          '&::after': { content: '"ORDERS TABLES REPORTS"', position: 'absolute', right: 20, bottom: 42 },
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: { xs: 'auto', md: 'calc(100svh - 36px)', lg: 'calc(100svh - 48px)' },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(340px, 0.94fr) minmax(380px, 1fr)', lg: 'minmax(480px, 1.02fr) minmax(480px, 0.98fr)' },
          gap: { xs: 2, md: 2.75, lg: 4 },
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            minHeight: { xs: 'auto', md: '100%' },
            p: { xs: 2.5, sm: 3, lg: 4.5 },
            borderRadius: { xs: '24px', md: '28px 64px 64px 28px', lg: '36px 90px 90px 36px' },
            overflow: 'hidden',
            display: 'grid',
            gridTemplateRows: { xs: 'auto auto auto', md: 'auto auto auto minmax(220px, 1fr) auto' },
            gap: { xs: 2.25, lg: 3 },
            background: isDark
              ? 'linear-gradient(104deg, rgba(11,35,17,0.96) 0 54%, rgba(21,48,24,0.92) 54% 100%)'
              : 'linear-gradient(104deg, rgba(246,252,245,0.96) 0 54%, rgba(227,241,225,0.88) 54% 100%)',
            boxShadow: isDark ? 'inset -1px 0 rgba(255,255,255,0.05)' : 'inset -1px 0 rgba(255,255,255,0.8)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: `linear-gradient(135deg, ${greenLight}, ${greenDark})`,
                boxShadow: '0 14px 28px rgba(22,127,33,0.24)',
              }}
            >
              <Restaurant sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography sx={{ color: isDark ? '#f8fafc' : ink, fontSize: 34, lineHeight: 1, fontWeight: 900 }}>
                Restaurant<Box component="span" sx={{ color: green }}>OS</Box>
              </Typography>
              <Typography sx={{ mt: 1, color: isDark ? '#cbd5e1' : '#334155', fontSize: 15, fontWeight: 700 }}>
                Smart Restaurant Management Platform
              </Typography>
            </Box>
          </Box>

          <Box sx={{ maxWidth: 620 }}>
            <Typography component="h1" sx={{ m: 0, color: isDark ? '#f8fafc' : ink, fontSize: { xs: 32, lg: 42 }, lineHeight: 1.14, fontWeight: 900 }}>
              Manage Your Restaurant <Box component="span" sx={{ color: green }}>Smarter</Box> Than Ever
            </Typography>
            <Typography sx={{ mt: 2.25, maxWidth: 480, color: isDark ? '#cbd5e1' : muted, fontSize: { xs: 16, lg: 20 }, lineHeight: 1.55, fontWeight: 700 }}>
              All-in-one solution for POS billing, kitchen management, inventory, and more.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, flexWrap: 'wrap' }}>
            {features.map((feature) => (
              <Box
                key={feature.label}
                sx={{
                  width: { xs: 86, lg: 94 },
                  minHeight: { xs: 84, lg: 94 },
                  p: 1,
                  display: 'grid',
                  placeItems: 'center',
                  alignContent: 'center',
                  gap: .75,
                  border: '1px solid rgba(255,255,255,0.74)',
                  borderRadius: '14px',
                  background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.76)',
                  boxShadow: isDark ? 'none' : '0 10px 24px rgba(15,23,42,0.08)',
                  textAlign: 'center',
                }}
              >
                <Box sx={{ color: green, '& svg': { fontSize: 26 } }}>{feature.icon}</Box>
                <Typography sx={{ color: isDark ? '#f8fafc' : ink, fontSize: 12, fontWeight: 900, lineHeight: 1.3 }}>
                  {feature.label}<br />{feature.detail}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              minHeight: 290,
              mx: { md: -3, lg: -4.5 },
              alignSelf: 'end',
              background: 'linear-gradient(0deg, rgba(8,65,20,0.32), rgba(255,255,255,0) 55%), url("/images/loginbackground.png") center / cover no-repeat',
              boxShadow: isDark ? 'inset 0 80px 100px rgba(7,19,11,0.45)' : 'inset 0 80px 100px rgba(246,252,245,0.48)',
            }}
          />

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignSelf: 'end',
              justifyContent: 'space-around',
              gap: 2,
              mt: -8,
              p: 3,
              borderRadius: '18px',
              color: '#fff',
              background: 'linear-gradient(135deg, rgba(10,91,26,0.98), rgba(4,55,19,0.98))',
              boxShadow: '0 22px 44px rgba(2,75,21,0.28)',
            }}
          >
            {[
              ['10,000+', 'Restaurants'],
              ['1M+', 'Orders Processed'],
              ['99.9%', 'Uptime'],
            ].map(([value, label]) => (
              <Box key={label} sx={{ px: 2, minWidth: 0 }}>
                <Typography sx={{ fontSize: 24, fontWeight: 900 }}>{value}</Typography>
                <Typography sx={{ fontSize: 14 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ minHeight: { xs: 'auto', md: '100%' }, display: 'grid', gridTemplateRows: 'auto minmax(0, auto) auto', alignContent: 'center', justifyItems: 'center', gap: 3 }}>
          <Box sx={{ width: 'min(600px, 100%)', display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 1.5 }}>
            <Button startIcon={<Language />} sx={topButtonSx(isDark)}>English</Button>
            <IconButton onClick={toggleMode} sx={topIconSx(isDark)} aria-label="Toggle theme">
              {isDark ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: 'min(600px, 100%)',
              p: { xs: 2.25, sm: 3, lg: 4.25 },
              display: 'grid',
              gap: 2.25,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
              borderRadius: { xs: '20px', lg: '28px' },
              background: isDark ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.93)',
              boxShadow: isDark ? '0 24px 70px rgba(0,0,0,0.36)' : '0 24px 70px rgba(15,23,42,0.12)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  mx: 'auto',
                  mb: 1.75,
                  border: '14px solid #eaf7e9',
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  background: `linear-gradient(135deg, ${greenLight}, ${greenDark})`,
                }}
              >
                <Restaurant sx={{ fontSize: 34 }} />
              </Box>
              <Typography sx={{ color: isDark ? '#f8fafc' : ink, fontSize: 30, lineHeight: 1, fontWeight: 900 }}>
                Restaurant<Box component="span" sx={{ color: green }}>OS</Box>
              </Typography>
              <Typography sx={{ display: 'inline-flex', mt: 2, mb: 2.25, px: 1.5, py: .75, color: green, borderRadius: 999, background: isDark ? 'rgba(35,141,37,0.16)' : '#edf8eb', fontSize: 13, fontWeight: 900 }}>
                Powered by AI
              </Typography>
              <Typography component="h2" sx={{ m: 0, color: isDark ? '#f8fafc' : ink, fontSize: 30, fontWeight: 900 }}>
                Welcome Back
              </Typography>
              <Typography sx={{ mt: 1, color: isDark ? '#cbd5e1' : muted, fontSize: 16, fontWeight: 700 }}>
                Sign in to continue managing your restaurant.
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>}

            <TextField
              label="Restaurant Code"
              value={restaurantCode}
              onChange={(e) => setRestaurantCode(e.target.value.toUpperCase())}
              fullWidth
              required
              InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }}
              sx={inputSx}
            />
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              required
              InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }}
              sx={inputSx}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((value) => !value)} aria-label="Toggle password visibility">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={rememberRestaurantCode} onChange={(e) => setRememberRestaurantCode(e.target.checked)} sx={{ color: green, '&.Mui-checked': { color: green } }} />}
                label={<Typography sx={{ color: isDark ? '#cbd5e1' : '#475467', fontSize: 14, fontWeight: 800 }}>Remember me</Typography>}
              />
              <Box component={Link} to="/register" sx={{ color: green, textDecoration: 'none', fontSize: 14, fontWeight: 900 }}>
                New account
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              endIcon={!isSubmitting ? <ArrowForward /> : undefined}
              sx={{
                minHeight: 58,
                borderRadius: '9px',
                fontSize: 17,
                fontWeight: 900,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${greenLight}, ${green})`,
                boxShadow: '0 16px 30px rgba(39,151,42,0.24)',
                '&:hover': { background: `linear-gradient(135deg, ${green}, ${greenDark})` },
              }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => fillDemo()} disabled={isSubmitting} sx={{ minHeight: 48, borderRadius: '9px', color: isDark ? '#f8fafc' : ink, borderColor: isDark ? 'rgba(255,255,255,0.18)' : '#d9dee7', textTransform: 'none', fontWeight: 900 }}>
                Demo Login
              </Button>
            </Box>

            <Box sx={{ mt: 1, p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, borderRadius: '14px', background: isDark ? 'rgba(35,141,37,0.12)' : 'linear-gradient(135deg, #eff8ef, #f8fbf8)' }}>
              {[
                { title: 'Call Support', value: '+91 123 456 7890', icon: <Phone /> },
                { title: 'WhatsApp', value: 'Chat Now', icon: <WhatsApp /> },
                { title: 'Email Support', value: 'support@restaurantos.com', icon: <Email /> },
              ].map((item) => (
                <Box key={item.title} sx={{ minWidth: 0, textAlign: 'center' }}>
                  <Box sx={{ color: green, '& svg': { fontSize: 20 } }}>{item.icon}</Box>
                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#334155', fontSize: 14, fontWeight: 900 }}>{item.title}</Typography>
                  <Typography sx={{ color: isDark ? '#cbd5e1' : '#475467', fontSize: 12, overflowWrap: 'anywhere' }}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Typography sx={{ color: isDark ? '#94a3b8' : muted, fontSize: 13, fontWeight: 700 }}>
            (c) 2026 RestaurantOS. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  function fillDemo() {
    setRestaurantCode('DEMO');
    setUsername('admin');
    setPassword('Demo@123');
    setRememberRestaurantCode(true);
  }
};

const topButtonSx = (isDark: boolean) => ({
  minHeight: 44,
  px: 2.5,
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
  borderRadius: 999,
  color: isDark ? '#f8fafc' : ink,
  background: isDark ? 'rgba(15,23,42,0.86)' : 'rgba(255,255,255,0.88)',
  boxShadow: isDark ? 'none' : '0 10px 24px rgba(15,23,42,0.08)',
  textTransform: 'none',
  fontWeight: 900,
});

const topIconSx = (isDark: boolean) => ({
  width: 44,
  height: 44,
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
  borderRadius: 999,
  color: isDark ? '#f8fafc' : ink,
  background: isDark ? 'rgba(15,23,42,0.86)' : 'rgba(255,255,255,0.88)',
  boxShadow: isDark ? 'none' : '0 10px 24px rgba(15,23,42,0.08)',
});

export default Login;
